import { ref, get, set, remove, onValue } from 'firebase/database';
import { useEffect, useState } from 'react';
import { db } from './config';

export type AccessCodeRole = 'cuidador' | 'usuario';

export type AccessCode = {
  code: string;
  role: AccessCodeRole;
  centerId: string;
  /** UID de la persona vinculada a este código (solo rol "usuario"). */
  residentUid?: string;
  label: string;
  createdAt: number;
  createdBy?: string;
  revoked?: boolean;
};

const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // sin 0,O,I,L,1
const STORAGE_KEY = 'gc_access_code';

export function generateCode(length = 7): string {
  let out = '';
  for (let i = 0; i < length; i++) out += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  return out;
}

function normalize(code: string): string {
  return code.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Migración legacy: 'residente' | 'cuidado' → 'usuario'. 'familiar' descartado.
 * Códigos revocados descartados.
 */
function normalizeRecord(v: unknown): AccessCode | null {
  if (!v || typeof v !== 'object') return null;
  const raw = v as Record<string, unknown>;
  if (raw.revoked) return null;
  const rawRole = raw.role;
  let role: AccessCodeRole;
  if (rawRole === 'cuidador') role = 'cuidador';
  else if (rawRole === 'usuario' || rawRole === 'cuidado' || rawRole === 'residente') role = 'usuario';
  else return null;
  const rec: AccessCode = {
    code: String(raw.code),
    role,
    centerId: String(raw.centerId || ''),
    label: String(raw.label || ''),
    createdAt: Number(raw.createdAt || 0),
  };
  if (raw.residentUid) rec.residentUid = String(raw.residentUid);
  if (raw.createdBy)   rec.createdBy   = String(raw.createdBy);
  return rec;
}

export async function createAccessCode(
  data: Omit<AccessCode, 'code' | 'createdAt'> & { length?: number },
): Promise<AccessCode> {
  const len = Math.min(8, Math.max(6, data.length || 7));
  for (let attempt = 0; attempt < 10; attempt++) {
    const code = generateCode(len);
    const snap = await get(ref(db, `access_codes/${code}`));
    if (snap.exists()) continue;
    const record: AccessCode = {
      code,
      role: data.role,
      centerId: data.centerId,
      label: data.label,
      createdAt: Date.now(),
    };
    if (data.residentUid) record.residentUid = data.residentUid;
    if (data.createdBy)   record.createdBy   = data.createdBy;
    await set(ref(db, `access_codes/${code}`), record);
    return record;
  }
  throw new Error('No se pudo generar un código único, inténtalo de nuevo');
}

export async function revokeAccessCode(code: string) {
  await remove(ref(db, `access_codes/${normalize(code)}`));
}

export async function fetchAccessCode(code: string): Promise<AccessCode | null> {
  const c = normalize(code);
  if (!c) return null;
  const snap = await get(ref(db, `access_codes/${c}`));
  if (!snap.exists()) return null;
  return normalizeRecord(snap.val());
}

export function useAccessCodes() {
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    return onValue(
      ref(db, 'access_codes'),
      (snap) => {
        const v = snap.val() as Record<string, unknown> | null;
        const list = v
          ? Object.values(v)
              .map(normalizeRecord)
              .filter((x): x is AccessCode => !!x)
              .sort((a, b) => b.createdAt - a.createdAt)
          : [];
        setCodes(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, []);
  return { codes, loading };
}

// ---------- Cleanup legacy DB ----------

/** Borra registros legacy de access_codes con role 'familiar' o revoked=true. */
export async function purgeLegacyCodes(): Promise<{ removed: number }> {
  const snap = await get(ref(db, 'access_codes'));
  const v = snap.val() as Record<string, Record<string, unknown>> | null;
  if (!v) return { removed: 0 };
  let removed = 0;
  for (const [code, rec] of Object.entries(v)) {
    if (rec?.role === 'familiar' || rec?.revoked) {
      await remove(ref(db, `access_codes/${code}`));
      removed++;
    }
  }
  return { removed };
}

// ---------- Storage ----------

export function storeAccessCode(c: AccessCode) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  localStorage.setItem('gc_role', c.role);
  if (c.role === 'usuario' && c.residentUid) {
    localStorage.setItem('gc_usuario_uid', c.residentUid);
    // Limpia claves legacy
    localStorage.removeItem('gc_cuidado_uid');
    localStorage.removeItem('gc_residente_uid');
  }
}

export function loadStoredAccessCode(): AccessCode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeRecord(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearStoredAccessCode() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('gc_role');
  // Nuevas
  localStorage.removeItem('gc_usuario_uid');
  localStorage.removeItem('gc_usuario_name');
  // Legacy
  localStorage.removeItem('gc_cuidado_uid');
  localStorage.removeItem('gc_cuidado_name');
  localStorage.removeItem('gc_residente_uid');
  localStorage.removeItem('gc_residente_name');
  localStorage.removeItem('gc_familiar_residents');
}
